import { customElement } from "lit/decorators.js";
import { html } from "lit";
import { groupStore, type ObservableGroup } from "../stores/groups.store";
import { z } from "zod";
import { FinalFormController, zodValidator } from "../controllers/final-form.controller";
import { consume } from "@lit/context";
import { navigatorContext, NavigatorController } from "../controllers/navigator.controller";
import { Page } from "../components/component";
import { localized, msg } from "@lit/localize";
import { ApiError } from "../error";
import { alertController } from "@ionic/core";

const formSchema = () =>
  z.object({
    section: z
      .string({
        required_error: msg("O nome é obrigatório"),
      })
      .min(2, msg("O nome deve ter no mínimo 2 caracteres"))
      .max(32, msg("O nome deve ter no máximo 32 caracteres"))
      .refine((value) => !["arquivados", "arquivado", "archived"].includes(value.toLowerCase()), {
        message: msg("Este nome de secção não é permitido"),
      }),
  });

type FormValues = z.infer<ReturnType<typeof formSchema>>;

@customElement("modal-create-section")
@localized()
export class ModalNewSection extends Page {
  group?: ObservableGroup;

  @consume({ context: navigatorContext })
  private navigator!: NavigatorController;

  #form = new FinalFormController<FormValues>(this, {
    validate: zodValidator(formSchema()),
    onSubmit: async (values) => {
      if (!this.group) {
        return;
      }

      try {
        await groupStore.createSection(this.group, {
          name: values.section,
          group: this.group.id,
          position: this.group.sections.length,
        });

        this.navigator.close();
      } catch (error) {
        if (error instanceof ApiError) {
          const alert = await alertController.create({
            header: msg("Erro"),
            message: error.message,
            buttons: ["OK"],
          });
          await alert.present();
        }
      }
    },
  });

  render() {
    const { form, register, renderError } = this.#form;
    const formState = form.getState();

    return html`
      <div class="inner-content">
        <ion-header>
          <ion-toolbar style="--border-color: #eaeaea">
            <ion-buttons slot="start">
              <ion-button @click=${() => this.navigator.close()} class="font-semibold">
                <span class="mt-[3px] -ml-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M16 16L12 12M12 12L8 8M12 12L16 8M12 12L8 16"
                      stroke="black"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </span>
                ${msg("fechar")}</ion-button
              >
            </ion-buttons>
            <ion-title class="font-display text-lg">${msg("Criar Seção")}</ion-title>
          </ion-toolbar>
        </ion-header>
        <form
          id="form-section-create"
          class="space-y-4 mt-4"
          @submit=${(e: Event) => {
            e.preventDefault();
            form.submit();
          }}>
          <ion-list lines="none">
            <div>
              <span class="font-semibold font-display">${msg("Nome")}</span>
              <ion-item class="mt-1">
                <ion-input
                  autocapitalize="on"
                  type="text"
                  placeholder=${msg("Secção")}
                  enterkeyhint="send"
                  ${register("section")}>
                </ion-input>
              </ion-item>
              ${renderError("section")}
            </div>
          </ion-list>

          <ion-button
            form="form-section-create"
            type="submit"
            color="primary"
            expand="block"
            shape="round"
            class="font-semibold"
            ?disabled=${formState.submitting}
            >${msg("Adicionar Secção")}</ion-button
          >
        </form>
      </div>
    `;
  }
}
